import { ConsoleLogger, request, type RequestOptions } from '@nextgen-web-framework/core';
import { v5 as uuidv5 } from 'uuid';
import {
  generateOtpReqObj,
  verifyOtpReqObj,
  resendOtpReqObj,
  checkUserExistanceReqObj,
  createPasswordReqObj,
  forgotAmwayIdReqObj,
  getJwtTokenReqObj,
  checkUserExistReqObj,
  updatePasswordReqObj,
  generateTokenReqObj,
  sendOtpWithChallengeIdReqObj,
  verifyOtpWithChallengeIdReqObj,
  generateChallengeIdForUpdatePasswordReqObj,
  delinkSocialAccountReqObj,
  getUserByProfileIdReqObj,
  generateWorkflowReqObj,
  createPasswordWithWorkflowIdReqObj,
  getCurrentlyLoggedInUserDetailsReqObj,
  updateUsernameChallengeIdForChangeAmwayIdReqObj,
  createChallengeIdForChangeAmwayIdReqObj,
  switchAccountReqObj,
  updateWorkflowReqForResetAmwayIdObj,
  submitWorkflowReqObj,
  getChallenges,
  createSocilaLinkChallengesObj,
  submitSocilaLinkChallengesObj,
  createSocialLinkLoggedInJansKey,
  getEnrollmentByProfileId,
  getAccountList,
  createEnrollment,
  updateUserStatusByProfileIdReqObj,
} from '../config/identityApiEndPoints';
import {
  GetAuthorizeUrlType,
  CheckUserExistanceInumResType,
  CheckUserExistanceResType,
  CreatePasswordReqType,
  CreatePasswordResType,
  ForgotAmwayIdReqPayload,
  GenotpErrorResponseHandler,
  LogoutReqPayload,
  IdentityResponse,
  BaseUrlReqPayloadType,
  ErrorHandlers,
  ChallengeIdReqType,
  ProfileIdReqType,
  SocialAccountReqType,
  WorkflowRequest,
  CorrelationidType,
  UpdateWorkflowRequest,
  SubmitWorkflowRequest,
  GenereateWorkflowResponse,
  UpdateWorkflowResponse,
  CustomRequestOptions,
  ChallengeResponse,
  CreateSocialLinkChallengeRequest,
  CreateSocialLinkChallengeResponse,
  SubmitSocialLinkChallengeRequest,
  AccountListResponse,
  EnrollmentRequest,
  UpdateUserStatusReqObject,
} from '../../types/identityTypes';
import {
  ScopeConstant,
  ResponseTypeConstant,
  AcrValueConstant,
  AuthPath,
  RequestId,
  Inum,
  LogoutPath,
  ChallengeId,
  Provider,
  BaseUrl,
  WorkFlowId,
  ProfileId,
  IsProfileUpdate,
} from '../config/identityApiConstants';
import { User } from '../models/User';
import { JansKeyResponse, UserResponse } from '../models/UserResponse';
import { OtpGeneration } from '../models/OtpGeneration';
import {
  ChallengesPostResponse,
  ChangePasswordRequest,
  GetUserResponse,
  RequestIdGenerated,
  VerifyOtp,
  WorkflowResponse,
} from '../models';
import { ChallengeIdPatchRequest } from '../models/ChallengeIdPatchRequest';
import { OtpResend } from '../models/OtpResend';
import { UpdatePasswordWithWorkflowRequest } from '../models/UpdatePasswordWithWorkflowRequest';
import { UpdatePasswordWorkflowIdResponse } from '../models/UpdatePasswordWorkflowIdResponse';
import { AuthenticateRequest } from '../models/AuthenticateRequest';
import { AlternateVouchingAuthenticateResponse, AuthenticateResponse } from '../models/AuthenticateResponse';
import { StepupChallengeResponse } from '../models/StepupChallengeResponse';
import { UpdatePasswordRequestWithChallengeId } from '../models/UpdatePasswordRequestWithChallengeId';
import { ChallengeIdPostRequest } from '../models/ChallengeIdPostRequest';
import { GetUserDetails } from '../models/GetUserDetails';
import { EnrollmentResponse } from '../models/EnrollmentResponse';

export const identityApi = {
  getAuthorizeUrl(req: GetAuthorizeUrlType): Promise<string> {
    const fallbackDefaultState = uuidv5(`${Date.now()}`, uuidv5.URL);
    const fallbackDefaultNonce = uuidv5(`${Date.now()}`, uuidv5.URL);
    const {
      gluuBaseUrl,
      redirectUri,
      clientId,
      scope = ScopeConstant,
      responseType = ResponseTypeConstant,
      acrValues = AcrValueConstant,
      state = fallbackDefaultState,
      nonce = fallbackDefaultNonce,
      prompt,
      loginHint,
      countryIso,
      bcAuthnToken,
      socialProvider,
      preselectedExternalProvider,
      socialConnectRedirectUrl,
      responseMode,
      jansKey,
    } = req;

    try {
      if (!redirectUri) {
        throw new Error('redirectUri is missing in request');
      } else if (!clientId) {
        throw new Error('clientId is missing in request');
      } else if (!gluuBaseUrl) {
        throw new Error('gluuBaseUrl is missing in request');
      }

      const queryParams = new URLSearchParams({
        scope,
        acr_values: acrValues,
        response_type: responseType,
        redirect_uri: redirectUri,
        state,
        nonce,
        client_id: clientId,
        ...(jansKey && { jansKey: jansKey }),
        ...(socialConnectRedirectUrl && {
          social_connect_redirect_url: socialConnectRedirectUrl,
        }),
        ...(socialProvider && { social_provider: socialProvider }),
        ...(prompt && { prompt }),
        ...(loginHint && { login_hint: loginHint }),
        ...(countryIso && { country_iso: countryIso }),
        ...(preselectedExternalProvider && { preselectedExternalProvider }),
        ...(bcAuthnToken && { bcAuthnToken }),
        ...(responseMode && { response_mode: responseMode }),
      });

      return Promise.resolve(`${gluuBaseUrl}/${AuthPath}?${queryParams.toString()}`);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async generateOtp(
    {
      otpModes,
      otpLength,
      errorHandlers,
      correlationid,
    }: OtpGeneration & CorrelationidType & GenotpErrorResponseHandler,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!otpModes) {
        throw new Error('otpModes is missing in request');
      } else if (!otpLength) {
        throw new Error('otpLength is missing in request');
      }

      const req = {
        ...generateOtpReqObj,
        body: {
          otpModes,
          otpLength,
          correlationid,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async verifyOtp(
    { otp, requestId }: VerifyOtp & RequestIdGenerated,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!otp) {
        throw new Error('otp is missing in request');
      } else if (!requestId) {
        throw new Error('requestId is missing in request');
      }
      const req = {
        ...verifyOtpReqObj,
        url: verifyOtpReqObj.url.replace(RequestId, requestId),
        body: {
          otp,
        } as unknown as BodyInit,
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async resendOtp({ requestId }: OtpResend, options?: CustomRequestOptions): Promise<IdentityResponse> {
    try {
      if (!requestId) {
        throw new Error('requestId is missing in request');
      }
      const req = {
        ...resendOtpReqObj,
        url: resendOtpReqObj.url.replace(RequestId, requestId),
        body: {} as unknown as BodyInit,
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async checkUserExistance({ username }: User, options?: CustomRequestOptions): Promise<CheckUserExistanceResType> {
    try {
      if (!username) {
        throw new Error('username is missing in request');
      }

      const req = {
        ...checkUserExistanceReqObj,
        params: { username },
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async createPassword(
    { payload, username }: CreatePasswordReqType,
    options?: CustomRequestOptions,
  ): Promise<CreatePasswordResType> {
    try {
      if (!payload?.password) {
        throw new Error('payload is missing in request');
      } else if (!username) {
        throw new Error('username is missing in request');
      }

      const getUserUrl = (inum: string) => `${inum}`;

      const data: CheckUserExistanceInumResType = await handleRequest({
        ...checkUserExistanceReqObj,
        params: { projection: 'full', username },
      });

      if (!data || !data.inum) {
        return Promise.reject('User not found');
      }

      const req = {
        ...createPasswordReqObj,
        url: createPasswordReqObj.url.replace(Inum, getUserUrl(data.inum)),
        body: payload as unknown as BodyInit,
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async getLogoutUrl(req: LogoutReqPayload): Promise<string> {
    try {
      const { gluuBaseUrl, idTokenHint, postLogoutRedirectUri, sessionState } = req;
      const queryParams = new URLSearchParams({
        ...(idTokenHint && { id_token_hint: idTokenHint }),
        ...(sessionState && { session_state: sessionState }),
        ...(postLogoutRedirectUri && {
          post_logout_redirect_uri: postLogoutRedirectUri,
        }),
      });
      return await Promise.resolve(`${gluuBaseUrl}/${LogoutPath}?${queryParams.toString()}`);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async forgotAmwayId(
    { username, errorHandlers }: ForgotAmwayIdReqPayload & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!username) {
        throw new Error('username is missing in request');
      }
      const req = {
        ...forgotAmwayIdReqObj,
        params: { username, projection: 'full' },
        ...(errorHandlers && { errorHandlers }),
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },
  async getJwtToken(payload: User, options?: CustomRequestOptions): Promise<UserResponse> {
    try {
      const { username } = payload;

      if (!username) {
        throw new Error('username is not found');
      }

      const req: RequestOptions = {
        ...getJwtTokenReqObj,
        body: payload as unknown as BodyInit,
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async checkUserExist({ username }: User, options?: CustomRequestOptions): Promise<CheckUserExistanceResType> {
    try {
      if (!username) {
        throw new Error('username is missing in request');
      }

      const req = {
        ...checkUserExistReqObj,
        params: {
          username,
        },
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async updatePassword(
    {
      currentPassword,
      challengeId,
      newPassword,
      errorHandlers,
      profileId,
    }: UpdatePasswordRequestWithChallengeId & ErrorHandlers & ProfileIdReqType,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!currentPassword) {
        throw new Error('currentPassword is missing in request');
      } else if (!newPassword) {
        throw new Error('newPassword is missing in request');
      }

      const req = {
        ...updatePasswordReqObj,
        url: updatePasswordReqObj.url.replace(ProfileId, `${profileId}`),
        body: {
          currentPassword,
          newPassword,
          challengeId,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async generateToken(
    { username, password, jansKey, errorHandlers }: AuthenticateRequest & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<AuthenticateResponse> {
    try {
      if (!username) {
        throw new Error('username is missing in request');
      } else if (!password) {
        throw new Error('password is missing in request');
      } else if (!jansKey) {
        throw new Error('jansKey is missing in request');
      }

      const queryParams = new URLSearchParams({
        username,
        password,
        jansKey,
      });
      const req = {
        ...generateTokenReqObj,
        body: queryParams,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async generateChallengeIdForUpdatePassword(
    { profileId }: ProfileIdReqType,
    options?: CustomRequestOptions,
  ): Promise<StepupChallengeResponse> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      }

      const req = {
        ...generateChallengeIdForUpdatePasswordReqObj,
        url: generateChallengeIdForUpdatePasswordReqObj.url.replace(ProfileId, profileId),
        body: {} as unknown as BodyInit,
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async sendOtpWithChallengeId(
    { challengeId }: ChallengeIdReqType,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!challengeId) {
        throw new Error('challengeId is missing in request');
      }
      const req = {
        ...sendOtpWithChallengeIdReqObj,
        url: sendOtpWithChallengeIdReqObj.url.replace(ChallengeId, challengeId),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async verifyOtpWithChallengeId(
    { code, challengeId }: ChallengeIdPatchRequest & ChallengeIdReqType,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!code) {
        throw new Error('code is missing in request');
      } else if (!challengeId) {
        throw new Error('challengeId is missing in request');
      }
      const req = {
        ...verifyOtpWithChallengeIdReqObj,
        url: verifyOtpWithChallengeIdReqObj.url.replace(ChallengeId, challengeId),
        body: {
          code: `${code}`,
        } as unknown as BodyInit,
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },
  async getChallengesWithChallengeId(
    { challengeId }: ChallengeIdReqType,
    options?: CustomRequestOptions,
  ): Promise<ChallengeResponse> {
    try {
      if (!challengeId) {
        throw new Error('challengeId is missing in request');
      }
      const req = {
        ...getChallenges,
        url: getChallenges.url.replace(ChallengeId, challengeId),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },
  async delinkSocialAccount(
    { provider, profileId }: SocialAccountReqType,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!provider) {
        throw new Error('provider is missing in request');
      } else if (!profileId) {
        throw new Error('profileId is missing in request');
      }

      const req = {
        ...delinkSocialAccountReqObj,
        url: delinkSocialAccountReqObj.url.replace(ProfileId, profileId).replace(Provider, provider),
        body: {
          profileId,
          provider,
        } as unknown as BodyInit,
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async getUserByProfileId({ profileId }: ProfileIdReqType, options?: CustomRequestOptions): Promise<IdentityResponse> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      }

      const req = {
        ...getUserByProfileIdReqObj,
        url: getUserByProfileIdReqObj.url.replace(ProfileId, profileId),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async getUserByProfileIdForOpsCenter(
    { profileId }: ProfileIdReqType,
    options?: CustomRequestOptions,
  ): Promise<GetUserResponse> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      }

      const req = {
        ...getUserByProfileIdReqObj,
        url: getUserByProfileIdReqObj.url.replace(ProfileId, profileId),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async updateUserStatusByProfileId(
    { profileId, amwayStatus }: ProfileIdReqType & UpdateUserStatusReqObject,
    options?: CustomRequestOptions,
  ): Promise<GetUserResponse> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      }

      const req = {
        ...updateUserStatusByProfileIdReqObj,
        url: updateUserStatusByProfileIdReqObj.url.replace(ProfileId, profileId),
        body: { amwayStatus } as unknown as BodyInit,
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async generateWorkflowId(
    { workflowType, username, baseUrl, errorHandlers }: BaseUrlReqPayloadType & WorkflowRequest & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!workflowType) {
        throw new Error('workflowType is missing in request');
      } else if (!username) {
        throw new Error('username is missing in request');
      }

      const req = {
        ...generateWorkflowReqObj,
        url: generateWorkflowReqObj.url.replace(BaseUrl, baseUrl),
        body: {
          workflowType,
          username,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },
  async createPasswordWithWorkflowId(
    {
      password,
      challengeId,
      workflowId,
      errorHandlers,
    }: UpdatePasswordWithWorkflowRequest & WorkflowResponse & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<UpdatePasswordWorkflowIdResponse> {
    try {
      if (!password) {
        throw new Error('password is missing in request');
      } else if (!challengeId) {
        throw new Error('challengeId is missing in request');
      } else if (!workflowId) {
        throw new Error('workflowId is missing in request');
      }
      const req = {
        ...createPasswordWithWorkflowIdReqObj,
        url: createPasswordWithWorkflowIdReqObj.url.replace(WorkFlowId, workflowId),
        body: { password, challengeId } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async getCurrentlyLoggedInUserDetails(
    { profileId }: ProfileIdReqType,
    options?: CustomRequestOptions,
  ): Promise<GetUserDetails> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      }
      const req = {
        ...getCurrentlyLoggedInUserDetailsReqObj,
        url: getCurrentlyLoggedInUserDetailsReqObj.url.replace(ProfileId, profileId),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async createChallengeIdForChangeAmwayId(
    { profileId, username }: ChangePasswordRequest & ProfileIdReqType,
    options?: CustomRequestOptions,
  ): Promise<ChallengesPostResponse> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      } else if (!username) {
        throw new Error('username is missing in request');
      }
      const req = {
        ...createChallengeIdForChangeAmwayIdReqObj,
        url: createChallengeIdForChangeAmwayIdReqObj.url.replace(ProfileId, profileId),
        body: { username } as unknown as BodyInit,
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async updateUsernameChallengeIdForChangeAmwayId(
    { profileId, challengeId }: ChallengeIdPostRequest & ProfileIdReqType,
    options?: CustomRequestOptions,
  ) {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      } else if (!challengeId) {
        throw new Error('challengeId is missing in request');
      }
      const req = {
        ...updateUsernameChallengeIdForChangeAmwayIdReqObj,
        url: updateUsernameChallengeIdForChangeAmwayIdReqObj.url.replace(ProfileId, profileId),
        body: { challengeId } as unknown as BodyInit,
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async switchAccount(
    { accountNumber, errorHandlers }: { accountNumber: number } & ErrorHandlers,
    options?: CustomRequestOptions,
  ) {
    try {
      const req = {
        ...switchAccountReqObj,
        body: { accountNumber } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async generateWorkflowIdForResetAmwayId(
    {
      workflowType,
      username,
      password,
      baseUrl,
      errorHandlers,
      idNumber,
      enrollmentToken,
    }: BaseUrlReqPayloadType & WorkflowRequest & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<GenereateWorkflowResponse> {
    try {
      if (!workflowType) {
        throw new Error('workflowType is missing in request');
      } else if (!baseUrl) {
        throw new Error('baseUrl is missing in request');
      }
      const req = {
        ...generateWorkflowReqObj,
        url: generateWorkflowReqObj.url.replace(BaseUrl, baseUrl),
        body: {
          workflowType,
          username,
          password,
          idNumber,
          enrollmentToken,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async updateWorkflowForResetAmwayId(
    {
      workflowId,
      selectedProfile,
      username,
      version,
      baseUrl,
      errorHandlers,
    }: BaseUrlReqPayloadType & UpdateWorkflowRequest & WorkflowResponse & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<UpdateWorkflowResponse> {
    try {
      if (!username) {
        throw new Error('username is missing in request');
      } else if (!workflowId) {
        throw new Error('workflowId is missing in request');
      } else if (!version) {
        throw new Error('version is missing in request');
      }

      const url = updateWorkflowReqForResetAmwayIdObj.url.replace(BaseUrl, baseUrl);

      const req = {
        ...updateWorkflowReqForResetAmwayIdObj,
        url: url.replace(WorkFlowId, workflowId),
        body: {
          selectedProfile,
          username,
          version,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async submitWorkflowForResetAmwayId(
    {
      workflowId,
      challengeId,
      password,
      baseUrl,
      errorHandlers,
    }: BaseUrlReqPayloadType & SubmitWorkflowRequest & WorkflowResponse & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<IdentityResponse> {
    try {
      if (!workflowId) {
        throw new Error('workflowId is missing in request');
      } else if (!password) {
        throw new Error('password is missing in request');
      }
      const url = submitWorkflowReqObj.url.replace(BaseUrl, baseUrl);

      const req = {
        ...submitWorkflowReqObj,
        url: url.replace(WorkFlowId, workflowId),
        body: {
          workflowId,
          challengeId,
          password,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };

      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async createSocialLinkChallenge(
    {
      aboNumber,
      idNumber,
      socialJansKey,
      baseUrl,
      errorHandlers,
    }: BaseUrlReqPayloadType & CreateSocialLinkChallengeRequest & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<CreateSocialLinkChallengeResponse> {
    try {
      if (!aboNumber) {
        throw new Error('aboNumber is missing in request');
      } else if (!idNumber) {
        throw new Error('idNumber is missing in request');
      } else if (!socialJansKey) {
        throw new Error('socialJansKey is missing in request');
      }

      const req = {
        ...createSocilaLinkChallengesObj,
        url: createSocilaLinkChallengesObj.url.replace(BaseUrl, baseUrl),
        body: {
          aboNumber,
          idNumber,
          socialJansKey,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async getAccountList({ profileId }: ProfileIdReqType, options?: CustomRequestOptions): Promise<AccountListResponse> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      }
      const req = {
        ...getAccountList,
        url: getAccountList.url.replace(ProfileId, profileId),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },
  async submitSocialLinkChallenge(
    {
      challengeId,
      socialJansKey,
      baseUrl,
      errorHandlers,
    }: BaseUrlReqPayloadType & SubmitSocialLinkChallengeRequest & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<AlternateVouchingAuthenticateResponse> {
    try {
      if (!challengeId) {
        throw new Error('challengeId is missing in request');
      } else if (!socialJansKey) {
        throw new Error('socialJansKey is missing in request');
      }
      const req = {
        ...submitSocilaLinkChallengesObj,
        url: submitSocilaLinkChallengesObj.url.replace(BaseUrl, baseUrl),
        body: {
          challengeId,
          socialJansKey,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async createSocialLinkLoggedInJansKey(baseUrl: string, options?: CustomRequestOptions): Promise<JansKeyResponse> {
    try {
      const req: RequestOptions = {
        ...createSocialLinkLoggedInJansKey,
        url: createSocialLinkLoggedInJansKey.url.replace(BaseUrl, baseUrl),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async getEnrollmentByProfileId(
    { profileId }: ProfileIdReqType,
    options?: CustomRequestOptions,
  ): Promise<EnrollmentResponse[]> {
    try {
      if (!profileId) {
        throw new Error('profileId is missing in request');
      }
      const req = {
        ...getEnrollmentByProfileId,
        url: getEnrollmentByProfileId.url.replace(ProfileId, profileId),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },

  async createEnrollment(
    {
      accountNumber,
      accountType,
      profileId,
      modeValue,
      isProfileUpdate,
      errorHandlers,
    }: EnrollmentRequest & ErrorHandlers,
    options?: CustomRequestOptions,
  ): Promise<EnrollmentResponse[]> {
    try {
      const req = {
        ...createEnrollment,
        url: createEnrollment.url.replace(IsProfileUpdate, isProfileUpdate as string),
        body: {
          accountNumber,
          accountType,
          profileId,
          modeValue,
        } as unknown as BodyInit,
        ...(errorHandlers && { errorHandlers }),
      };
      return await handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      throw error;
    }
  },
};

async function handleRequest<T>(req: RequestOptions, options?: CustomRequestOptions): Promise<T> {
  if (options?.headers) {
    req.headers = options?.headers;
  }
  if (options?.baseURL) {
    req.url = `${options.baseURL}${req.url}`;
  }
  const response: T = await request(req, {
    skipIdempotency: false,
    customCache: true,
    customKeyGenerator: () => uuidv5(`${Date.now()}`, uuidv5.URL),
  });
  return Promise.resolve(response);
}

export default identityApi;
